import type { FC, SVGProps } from 'react';

/**
 * Miniature type sketches for the wizard's chart-type gallery, one per chart
 * type. Drawn with the real chart tints so a card reads as the type it picks.
 * Purely decorative (aria-hidden) — the card's text label names the type.
 */

const THUMB = {
    accent: '#3D52D9',
    ref: '#94A3B8',
    band: 'rgba(61,82,217,0.12)',
    cutoff: '#D6491F',
    ink: '#94A3B8',
} as const;

const ThumbFrame: FC<SVGProps<SVGSVGElement>> = ({ children, ...props }) => (
    <svg viewBox="0 0 64 40" fill="none" aria-hidden="true" {...props}>
        {children}
    </svg>
);

export const BarsThumb: FC<SVGProps<SVGSVGElement>> = (props) => (
    <ThumbFrame {...props}>
        <rect x="2" y="14" width="60" height="14" fill={THUMB.band} />
        {[
            [7, 10, 26],
            [17, 22, 14],
            [27, 16, 20],
            [37, 6, 30],
            [47, 18, 18],
            [57, 12, 24],
        ].map(([x, y, h]) => (
            <rect key={x} x={x - 3} y={y} width="6" height={h} rx="1.5" fill={THUMB.accent} />
        ))}
    </ThumbFrame>
);

export const LineThumb: FC<SVGProps<SVGSVGElement>> = (props) => (
    <ThumbFrame {...props}>
        <polyline
            points="6,26 16,12 26,18 36,22 46,10 58,14"
            stroke={THUMB.accent}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
        />
        {[
            [6, 26],
            [16, 12],
            [26, 18],
            [36, 22],
            [46, 10],
            [58, 14],
        ].map(([cx, cy]) => (
            <circle
                key={`${cx}-${cy}`}
                cx={cx}
                cy={cy}
                r="2.4"
                fill={THUMB.accent}
                stroke="#fff"
                strokeWidth="1.2"
            />
        ))}
    </ThumbFrame>
);

export const RadarThumb: FC<SVGProps<SVGSVGElement>> = (props) => (
    <ThumbFrame {...props}>
        <polygon
            points="32,4 50,13 50,29 32,37 14,29 14,13"
            stroke={THUMB.ink}
            strokeWidth="1"
            fill="none"
        />
        <polygon
            points="32,12 43,16 41,27 32,31 21,26 23,17"
            stroke={THUMB.accent}
            strokeWidth="2"
            strokeLinejoin="round"
            fill="rgba(61,82,217,0.12)"
        />
    </ThumbFrame>
);

export const ScatterThumb: FC<SVGProps<SVGSVGElement>> = (props) => (
    <ThumbFrame {...props}>
        {[
            [8, 28],
            [17, 18],
            [25, 24],
            [33, 10],
            [41, 20],
            [50, 14],
            [57, 26],
        ].map(([cx, cy]) => (
            <circle
                key={`${cx}-${cy}`}
                cx={cx}
                cy={cy}
                r="2.8"
                fill={THUMB.accent}
                stroke="#fff"
                strokeWidth="1.2"
            />
        ))}
    </ThumbFrame>
);

export const QuadrantThumb: FC<SVGProps<SVGSVGElement>> = (props) => (
    <ThumbFrame {...props}>
        <line
            x1="32"
            y1="4"
            x2="32"
            y2="36"
            stroke={THUMB.ink}
            strokeWidth="1"
            strokeDasharray="3 2"
        />
        <line
            x1="4"
            y1="20"
            x2="60"
            y2="20"
            stroke={THUMB.ink}
            strokeWidth="1"
            strokeDasharray="3 2"
        />
        <circle cx="42" cy="13" r="3.5" fill={THUMB.accent} stroke="#fff" strokeWidth="1.2" />
    </ThumbFrame>
);

export const NormalCurveThumb: FC<SVGProps<SVGSVGElement>> = (props) => (
    <ThumbFrame {...props}>
        {/* Classification band stripes behind the bell (tail → center → tail). */}
        <rect x="2" y="4" width="14" height="30" fill="rgba(100,116,139,0.14)" />
        <rect x="16" y="4" width="10" height="30" fill="rgba(100,116,139,0.08)" />
        <rect x="38" y="4" width="10" height="30" fill="rgba(100,116,139,0.08)" />
        <rect x="48" y="4" width="14" height="30" fill="rgba(100,116,139,0.14)" />
        <path
            d="M2 34 C 18 34, 22 6, 32 6 C 42 6, 46 34, 62 34"
            stroke={THUMB.accent}
            strokeWidth="2"
            fill="rgba(61,82,217,0.10)"
        />
        <line
            x1="42"
            y1="8"
            x2="42"
            y2="34"
            stroke={THUMB.cutoff}
            strokeWidth="1.5"
            strokeDasharray="3 2"
        />
    </ThumbFrame>
);

export const GaugeThumb: FC<SVGProps<SVGSVGElement>> = (props) => (
    <ThumbFrame {...props}>
        {/* Outer zone ring + inner round-capped progress arc (no pointer). */}
        <path
            d="M6 35 A 26 26 0 0 1 58 35"
            stroke={THUMB.band}
            strokeWidth="3.5"
            fill="none"
            strokeLinecap="round"
        />
        <path
            d="M12 35 A 20 20 0 0 1 52 35"
            stroke="rgba(61,82,217,0.10)"
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
        />
        <path
            d="M12 35 A 20 20 0 0 1 39 16"
            stroke={THUMB.accent}
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
        />
    </ThumbFrame>
);

export const BulletThumb: FC<SVGProps<SVGSVGElement>> = (props) => (
    <ThumbFrame {...props}>
        <rect x="4" y="14" width="56" height="12" rx="3" fill={THUMB.band} />
        <rect x="4" y="17" width="38" height="6" rx="3" fill={THUMB.accent} />
        <line x1="48" y1="11" x2="48" y2="29" stroke={THUMB.cutoff} strokeWidth="1.5" />
    </ThumbFrame>
);

export const ClassificationThumb: FC<SVGProps<SVGSVGElement>> = (props) => (
    <ThumbFrame {...props}>
        <rect x="2" y="6" width="20" height="28" fill="rgba(61,82,217,0.08)" />
        <rect x="22" y="6" width="22" height="28" fill="rgba(61,82,217,0.16)" />
        <rect x="44" y="6" width="18" height="28" fill="rgba(61,82,217,0.26)" />
        <rect x="4" y="9" width="44" height="5" rx="2.5" fill={THUMB.accent} />
        <rect x="4" y="18" width="24" height="5" rx="2.5" fill={THUMB.accent} />
        <rect x="4" y="27" width="34" height="5" rx="2.5" fill={THUMB.accent} />
    </ThumbFrame>
);
